import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowRight, Check, Server, Cloud, Zap, Shield, Database, HardDrive } from "lucide-react";
import { HOSTING_PLANS, HOSTING_COMPARE } from "@/data/hosting";
import TechBg from "@/components/TechBg";

const planIcon = {
  shared: HardDrive,
  business: Server,
  vps: Database,
  managed: Shield,
  cloud: Cloud,
  dedicated: Zap,
};

export default function Hosting() {
  return (
    <div data-testid="hosting-page">
      {/* HERO */}
      <section className="relative overflow-hidden border-b border-[#1f1f22]">
        <TechBg />
        <div className="container-x relative pt-20 md:pt-28 pb-20 z-10">
          <div className="label-mono mb-6">// Hosting & Infrastructure</div>
          <h1 className="font-display text-[clamp(40px,6.5vw,84px)] font-black uppercase tracking-tight leading-[0.94] text-white max-w-5xl">
            Hosting that<br /><span className="text-[#DC1F33]">never sleeps.</span>
          </h1>
          <p className="mt-8 text-lg text-zinc-400 max-w-2xl leading-relaxed">
            From shared hosting for your first website to enterprise multi-region cloud deployments — fully managed, 24/7 monitored, daily backed up. Pick a plan or talk to us for custom infra.
          </p>
          <div className="mt-10 flex flex-wrap gap-4">
            <a href="#plans" data-testid="hosting-see-plans" className="btn-primary">See Plans <ArrowRight size={16} /></a>
            <Link to="/contact" data-testid="hosting-custom-cta" className="btn-ghost">Custom Quote <ArrowUpRight size={16} /></Link>
          </div>
        </div>
      </section>

      {/* QUICK STATS BAR */}
      <section className="border-b border-[#1f1f22] bg-[#0B0B0D]">
        <div className="container-x">
          <div className="grid grid-cols-2 md:grid-cols-4 border-l border-t border-[#1f1f22]">
            {[
              { v: "99.99%", l: "Uptime SLA" },
              { v: "<30 min", l: "Incident response" },
              { v: "Daily", l: "Automated backups" },
              { v: "24/7", l: "Human support" },
            ].map((s) => (
              <div key={s.l} className="grid-cell text-center md:text-left">
                <div className="font-display text-3xl md:text-4xl font-black text-white tracking-tight">{s.v}</div>
                <div className="label-mono mt-2">{s.l}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PRICING PLANS */}
      <section id="plans" className="section border-b border-[#1f1f22]">
        <div className="container-x">
          <div className="label-mono mb-4">// Pricing</div>
          <h2 className="font-display text-4xl md:text-5xl font-black uppercase tracking-tight text-white leading-[0.95] max-w-3xl mb-4">
            Simple plans. <span className="text-[#DC1F33]">No surprises.</span>
          </h2>
          <p className="text-zinc-400 max-w-2xl mb-14">Every plan includes free SSL, daily backups and 24/7 human support. Cancel anytime.</p>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-[#1f1f22] border border-[#1f1f22]">
            {HOSTING_PLANS.map((p) => {
              const Icon = planIcon[p.slug] || Server;
              return (
                <div
                  key={p.slug}
                  data-testid={`plan-${p.slug}`}
                  className={`relative bg-[#0B0B0D] p-8 flex flex-col ${p.popular ? "ring-1 ring-[#DC1F33] ring-offset-0" : ""}`}
                >
                  {p.popular && (
                    <div className="absolute top-0 right-0 bg-[#DC1F33] text-white font-display font-bold text-[10px] tracking-widest uppercase px-3 py-1.5">
                      Most popular
                    </div>
                  )}
                  <div className="flex items-center gap-3 mb-5">
                    <div className="w-11 h-11 border border-[#27272A] flex items-center justify-center text-[#DC1F33]">
                      <Icon size={20} />
                    </div>
                    <div>
                      <div className="font-display text-2xl font-bold text-white tracking-tight">{p.name}</div>
                      <div className="label-mono">{p.tagline}</div>
                    </div>
                  </div>
                  <div className="mb-6 flex items-baseline gap-1">
                    <span className="font-display text-5xl font-black text-white">{p.price}</span>
                    <span className="label-mono">{p.period}</span>
                  </div>
                  <ul className="space-y-3 mb-8 flex-1">
                    {p.features.map((f) => (
                      <li key={f} className="flex items-start gap-3 text-sm text-zinc-300">
                        <Check size={16} className="text-[#DC1F33] mt-0.5 shrink-0" />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                  <Link
                    to="/contact"
                    state={{ plan: p.name }}
                    data-testid={`plan-cta-${p.slug}`}
                    className={p.popular ? "btn-primary" : "btn-ghost"}
                  >
                    {p.price === "Custom" ? "Talk to Sales" : `Choose ${p.name}`} <ArrowUpRight size={16} />
                  </Link>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* COMPARE TABLE */}
      <section className="section border-b border-[#1f1f22] bg-[#0B0B0D]">
        <div className="container-x">
          <div className="label-mono mb-4">// Compare plans</div>
          <h2 className="font-display text-4xl md:text-5xl font-black uppercase tracking-tight text-white leading-[0.95] mb-12">Feature-by-feature.</h2>

          <div className="overflow-x-auto -mx-6 px-6 md:mx-0 md:px-0">
            <table className="w-full min-w-[820px] border border-[#1f1f22] text-sm">
              <thead>
                <tr className="bg-[#0E0E10]">
                  <th className="text-left p-4 border-r border-[#1f1f22] label-mono">Feature</th>
                  {HOSTING_PLANS.map((p) => (
                    <th key={p.slug} className="p-4 font-display text-white border-r border-[#1f1f22] last:border-r-0">{p.name}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {HOSTING_COMPARE.map((row, i) => (
                  <tr key={row.f} className={i % 2 === 0 ? "bg-[#0A0A0C]" : "bg-[#0B0B0D]"}>
                    <td className="p-4 border-t border-r border-[#1f1f22] text-zinc-400 font-display">{row.f}</td>
                    {HOSTING_PLANS.map((p) => (
                      <td key={p.slug} className="p-4 border-t border-r border-[#1f1f22] last:border-r-0 text-zinc-200 text-center">
                        {row.plans[p.slug]}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* INCLUDED IN ALL */}
      <section className="section border-b border-[#1f1f22]">
        <div className="container-x">
          <div className="label-mono mb-4">// Included in every plan</div>
          <h2 className="font-display text-4xl md:text-5xl font-black uppercase tracking-tight text-white leading-[0.95] mb-14 max-w-3xl">
            Production-grade defaults<br /><span className="text-zinc-500">on day one.</span>
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 border-l border-t border-[#1f1f22]">
            {[
              { icon: Shield, t: "Free SSL", d: "Let's Encrypt auto-renewal." },
              { icon: HardDrive, t: "Daily Backups", d: "30-day retention, 1-click restore." },
              { icon: Zap, t: "Global CDN", d: "Cloudflare or AWS CloudFront." },
              { icon: Server, t: "DDoS Protection", d: "Layer 3 / 4 / 7 mitigation." },
              { icon: Database, t: "Managed DB", d: "PostgreSQL, MySQL or MongoDB." },
              { icon: Cloud, t: "Monitoring", d: "Uptime + performance dashboards." },
              { icon: Check, t: "Free Migrations", d: "We move your existing site for free." },
              { icon: ArrowRight, t: "Easy Scale-Up", d: "Upgrade with zero downtime." },
            ].map((x) => {
              const I = x.icon;
              return (
                <div key={x.t} className="grid-cell">
                  <I size={20} className="text-[#DC1F33] mb-4" />
                  <div className="font-display text-lg font-bold text-white mb-2">{x.t}</div>
                  <div className="text-sm text-zinc-400">{x.d}</div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section">
        <div className="container-x">
          <div className="border border-[#27272A] p-12 md:p-16 bg-[#0E0E10] flex flex-col md:flex-row md:items-center justify-between gap-8">
            <div>
              <div className="label-mono mb-3">// Need help choosing</div>
              <h3 className="font-display text-3xl md:text-4xl font-black uppercase tracking-tight text-white leading-tight">Not sure which plan?</h3>
              <p className="text-zinc-400 mt-3 max-w-xl">Tell us your stack and traffic — we'll recommend the right plan and migrate you over for free.</p>
            </div>
            <Link to="/contact" data-testid="hosting-final-cta" className="btn-primary whitespace-nowrap">
              Talk to an Engineer <ArrowUpRight size={16} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
